import {
  extractAxiosErrorMessage,
  useAsyncOperation,
  useSiteHealthInvestigation,
} from "@inrange/building-manager-api-client";
import { formatRelativeTime } from "@inrange/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Spinner } from "react-bootstrap";
import AllInvestigationsModal from "./AllInvestigationsModal";
import InvestigationStatusBadge from "./InvestigationStatusBadge";
import InvestigationThreadModal from "./InvestigationThreadModal";
import {
  groupInvestigationSessions,
  InvestigationSession,
} from "./investigationSessions";

const TILE_SESSION_COUNT = 3;

const COALESCED_NOTICE =
  "An investigation was already running for this site, so your question wasn't started - this is that run's progress. Send it again once the run finishes.";

// The LRO result. `status: "failed"` is an AI-model configuration problem: the
// run is never persisted, so it has no row in the investigations list and has
// to surface here instead.
interface InvestigationOperationResult {
  runId?: string;
  sessionId?: string;
  status?: string;
  error?: string;
}

// The turn the operator has just sent, held in local state so the thread can
// show the question and live progress before any row exists server-side.
interface PendingTurn {
  sessionId: string | null;
  question: string | null;
}

// The thread and the all-sessions list are two modes of one modal surface, so
// they're one piece of state: the operator moves between them without the card
// having to close one modal and open the other, and nothing else on the card
// (least of all the poller driving an in-flight run) is touched by navigating.
type ModalView = "none" | "thread" | "list";

const HealthInvestigationCard = ({
  siteId,
  siteName,
  timeZone,
}: {
  siteId: string;
  siteName: string;
  timeZone: string;
}) => {
  const queryClient = useQueryClient();
  const { investigationsQuery, startInvestigation } =
    useSiteHealthInvestigation(siteId);
  const [modalView, setModalView] = useState<ModalView>("none");
  // Separate from `modalView` because a brand-new session has no id until the
  // kickoff response comes back.
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Kickoff-mutation errors render inside the thread modal (which stays open so
  // the operator can correct the question and retry); `errorMessage` above is
  // for LRO failures/timeouts, which also surface on the tile.
  const [startErrorMessage, setStartErrorMessage] = useState<string | null>(
    null
  );
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);
  // Async-poll state for the investigation LRO (the AxpoInvoicesCard
  // pendingOperation pattern): the kickoff 202 hands back the operationKey,
  // useAsyncOperation polls /async_status until the run finishes. `startedAt`
  // guards against the operation key being reused - the backend uses a fixed
  // key per site, so the poller can still hold the *previous* run's completed
  // result for a moment after a new run starts.
  const [pendingOperation, setPendingOperation] = useState<{
    siteId: string;
    operationKey: string;
    startedAt: number;
  } | null>(null);
  const [pendingTurn, setPendingTurn] = useState<PendingTurn | null>(null);

  // Adopt a run another tab or machine started, as reported by the investigations list.
  // Latched per fetch of that list: a cached list can name a run that has since
  // finished, and the quiet drop below would otherwise re-adopt it in a loop.
  const activeOperationKey =
    investigationsQuery.data?.activeOperationKey ?? null;
  const listUpdatedAt = investigationsQuery.dataUpdatedAt;
  const adoptedForListAt = useRef<number | null>(null);
  useEffect(() => {
    if (pendingOperation || !siteId || !activeOperationKey) return;
    if (adoptedForListAt.current === listUpdatedAt) return;
    adoptedForListAt.current = listUpdatedAt;
    // startedAt 0 marks it as not ours, so the guards below drop it quietly if it has already finished.
    setPendingOperation({
      siteId,
      operationKey: activeOperationKey,
      startedAt: 0,
    });
    // No question is known for a run we didn't start; the thread modal renders that shape already.
    setPendingTurn({ sessionId: null, question: null });
  }, [siteId, pendingOperation, activeOperationKey, listUpdatedAt]);

  const asyncOperation = useAsyncOperation({
    siteId: pendingOperation?.siteId ?? "",
    operationKey: pendingOperation?.operationKey ?? null,
  });
  const { data: asyncData, dataUpdatedAt, timedOut } = asyncOperation;

  // Whether THIS operation was ever observed in flight. An adopted run may have finished
  // after the list naming it was fetched; announcing that would raise a stale banner.
  // Reset per operation, since adoption can now follow any refetch of the list.
  const sawRunning = useRef(false);
  useEffect(() => {
    sawRunning.current = false;
  }, [pendingOperation]);
  useEffect(() => {
    if (asyncData?.status === "pending" || asyncData?.status === "running") {
      sawRunning.current = true;
    }
  }, [asyncData?.status]);

  useEffect(() => {
    if (!pendingOperation || !asyncData) return;
    // Ignore a poll result that predates this run - it belongs to the previous
    // use of the same operation key.
    if (dataUpdatedAt < pendingOperation.startedAt) return;
    const status = asyncData.status;
    if (status !== "completed" && status !== "failed") return;
    // Adopted, and already finished when we found it: there is nothing to
    // announce. Let it go quietly rather than reporting someone else's old run.
    if (pendingOperation.startedAt === 0 && !sawRunning.current) {
      setPendingOperation(null);
      setPendingTurn(null);
      return;
    }
    if (status === "failed") {
      setErrorMessage(asyncData.error ?? "Investigation failed.");
      setPendingOperation(null);
      setPendingTurn(null);
      return;
    }
    const result = asyncData.result as InvestigationOperationResult | undefined;
    if (result?.status === "failed") {
      setErrorMessage(result.error ?? "Investigation failed.");
      setPendingOperation(null);
      setPendingTurn(null);
      return;
    }
    if (result?.sessionId) setOpenSessionId(result.sessionId);
    // Hold the optimistic question and its spinner on screen until the
    // refetched list carries the finished run, so the thread doesn't flicker
    // between the two representations of the same turn.
    void queryClient
      .invalidateQueries(["siteHealthInvestigations", siteId])
      .finally(() => {
        setPendingOperation(null);
        setPendingTurn(null);
      });
  }, [asyncData, dataUpdatedAt, pendingOperation, queryClient, siteId]);

  useEffect(() => {
    if (timedOut && pendingOperation) {
      // An adopted run we never saw running is someone else's: drop it quietly rather
      // than raise a timeout banner on an investigation this operator didn't start.
      if (pendingOperation.startedAt === 0 && !sawRunning.current) {
        setPendingOperation(null);
        setPendingTurn(null);
        return;
      }
      setErrorMessage("Investigation timed out after 15 minutes.");
      setPendingOperation(null);
      setPendingTurn(null);
    }
  }, [timedOut, pendingOperation]);

  const sessions: InvestigationSession[] = useMemo(
    () =>
      groupInvestigationSessions(
        investigationsQuery.data?.investigations ?? []
      ),
    [investigationsQuery.data]
  );
  const openSession =
    sessions.find((s) => s.sessionId === openSessionId) ?? null;

  // "Running" is what the poll says, not that we hold a key: an adopted run may have
  // finished since the list was fetched, so show nothing until the first poll answers.
  const isRunning =
    pendingOperation !== null &&
    (asyncData
      ? asyncData.status === "pending" || asyncData.status === "running"
      : pendingOperation.startedAt > 0);
  // The detailed line - the tool call the agent is on right now - for the modal,
  // which is where someone actually watches a run.
  const progressMessage = isRunning
    ? (asyncData?.progress?.message ??
      (asyncData?.status === "running" ? "Investigating..." : "Queued..."))
    : null;
  // The card gets a short, fixed-width one instead. It is a summary of the site's
  // investigations, not a console: a tool call line is often a whole SQL
  // statement, which wrapped over five lines and pushed the buttons off the card.
  // The step count keeps it obviously alive without varying in width.
  // Counted from the tool events rather than progress.current, which is capped at
  // the nominal total because it also drives a progress bar - so it saturates and
  // stops moving on a long run, which is exactly when someone is checking whether
  // it is still going. Falls back to the counter for a run with no event feed.
  const stepCount =
    asyncData?.events?.filter((e) => e.kind === "tool").length ??
    asyncData?.progress?.current ??
    0;
  const cardProgressLabel = !isRunning
    ? null
    : asyncData?.status === "running"
      ? `Investigating... (${stepCount} steps)`
      : "Queued...";
  // The thread shows the pending turn only when it belongs to the session on
  // screen (a new session's turn has no id until its answer lands).
  const isPendingHere =
    isRunning &&
    pendingTurn !== null &&
    (pendingTurn.sessionId === null
      ? openSessionId === null
      : pendingTurn.sessionId === openSessionId);

  const handleSend = async (question: string): Promise<boolean> => {
    setErrorMessage(null);
    setStartErrorMessage(null);
    setNoticeMessage(null);
    try {
      const response = await startInvestigation.mutateAsync({
        focus: question || undefined,
        parentRunId: openSession?.latestRun.runId,
      });
      setPendingOperation({
        siteId,
        operationKey: response.operationKey,
        startedAt: Date.now(),
      });
      if (response.sessionId) setOpenSessionId(response.sessionId);
      if (response.coalesced) {
        // The question was not started - follow the run that is already going
        // and leave the text in the box so the operator can resend it.
        setNoticeMessage(COALESCED_NOTICE);
        setPendingTurn({
          sessionId: response.sessionId ?? null,
          question: null,
        });
        return false;
      }
      setPendingTurn({
        sessionId: response.sessionId ?? null,
        question: question || null,
      });
      return true;
    } catch (err) {
      const message = await extractAxiosErrorMessage(err);
      setStartErrorMessage(`Failed to start investigation: ${message}`);
      return false;
    }
  };

  const openSessionThread = (sessionId: string) => {
    setStartErrorMessage(null);
    setNoticeMessage(null);
    setOpenSessionId(sessionId);
    setModalView("thread");
  };

  const openNewThread = () => {
    setStartErrorMessage(null);
    setNoticeMessage(null);
    setOpenSessionId(null);
    setModalView("thread");
  };

  // Re-point the thread at the session the in-flight run belongs to, so the
  // shortcut lands on the live progress however the operator navigated away.
  const openRunningThread = () => {
    setOpenSessionId(pendingTurn?.sessionId ?? null);
    setModalView("thread");
  };

  const tileSessions = sessions.slice(0, TILE_SESSION_COUNT);

  return (
    <>
      <Card body>
        <h6 className="fw-bold mb-2">AI investigation</h6>
        {investigationsQuery.isLoading ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <div className="d-flex flex-column gap-2">
            {tileSessions.length === 0 ? (
              <div className="text-muted small">Never run</div>
            ) : (
              <div className="d-flex flex-column gap-1">
                {tileSessions.map((session) => (
                  <Button
                    key={session.sessionId}
                    variant="link"
                    size="sm"
                    className="text-start p-0 text-decoration-none d-block w-100"
                    onClick={() => openSessionThread(session.sessionId)}
                  >
                    <div className="small text-truncate">{session.title}</div>
                    <div className="d-flex align-items-center gap-2">
                      <InvestigationStatusBadge
                        status={session.latestRun.status}
                      />
                      <span className="text-muted small">
                        {formatRelativeTime(session.latestRun.createdAt)}
                      </span>
                    </div>
                  </Button>
                ))}
              </div>
            )}
            {isRunning && (
              <Button
                variant="link"
                size="sm"
                className="text-start p-0 text-decoration-none text-muted"
                onClick={openRunningThread}
              >
                <span className="d-flex align-items-center gap-2 small">
                  <Spinner animation="border" size="sm" />
                  <span>{cardProgressLabel}</span>
                </span>
              </Button>
            )}
            {errorMessage && (
              <div className="text-danger small">{errorMessage}</div>
            )}
            {/* Both entry points are always offered, whether or not this site
                has ever been investigated. */}
            <div className="d-flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline-primary"
                disabled={isRunning}
                title={
                  isRunning
                    ? "An investigation is already running for this site"
                    : undefined
                }
                onClick={openNewThread}
              >
                New investigation
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setModalView("list")}
              >
                All investigations
                {sessions.length > 0 ? ` (${sessions.length})` : ""}
              </Button>
            </div>
          </div>
        )}
      </Card>
      {modalView === "thread" && (
        <InvestigationThreadModal
          siteId={siteId}
          siteName={siteName}
          timeZone={timeZone}
          session={openSession}
          pendingQuestion={pendingTurn?.question ?? null}
          isPending={isPendingHere}
          isOtherRunPending={isRunning && !isPendingHere}
          progressMessage={progressMessage}
          events={asyncData?.events}
          errorMessage={errorMessage}
          startErrorMessage={startErrorMessage}
          noticeMessage={noticeMessage}
          isSending={startInvestigation.isLoading}
          onSend={handleSend}
          onBack={() => setModalView("list")}
          onHide={() => setModalView("none")}
        />
      )}
      {modalView === "list" && (
        <AllInvestigationsModal
          siteName={siteName}
          sessions={sessions}
          timeZone={timeZone}
          onSelect={openSessionThread}
          onHide={() => setModalView("none")}
        />
      )}
    </>
  );
};

export default HealthInvestigationCard;
